import { useSyncExternalStore } from 'react';

/**
 * Whether a `@clerk/clerk-js` UI chunk (e.g. the `userbutton` chunk that
 * `<UserButton>` needs to mount) has failed to load on this page.
 *
 * WHY THIS EXISTS. Incident 2026-08-20 (user-2f73bc@example.com, Safari
 * 26.5, app.overlayqa.com/dashboard): `Loading chunk 2270 failed. (error:
 * .../@clerk/clerk-js@5.127.2/dist/userbutton_clerk.browser_...js)`.
 * `<UserButton>` renders nothing when its chunk fails, and it is the ONLY
 * sign-out control in the dashboard shell (grepped: Layout.tsx is its only
 * render site) — so a chunk failure strands the user signed in with no way
 * out short of clearing cookies by hand.
 *
 * THE MECHANISM. clerk-js is cross-origin, so the browser sanitizes the
 * failure before it reaches any `window` error/rejection listener: proven
 * live 2026-08-20 in real Chromium across four failure modes (abort/failed,
 * abort/timedout, a 40s hang, and a 500 with no CORS headers), all four
 * delivering exactly `message: "Script error.", error: null, target: window`
 * and nothing else on `window`. The full `ChunkLoadError` text exists only on
 * `console.error` (which is why PostHog's console-error autocapture can see
 * it) and on a debugger's own `pageerror` channel — never on `window`. A
 * detector keyed on `event.message` (what PostHog bot PR #83 shipped) can
 * therefore never fire for this failure class: its 13 unit tests passed only
 * because they dispatched synthetic events carrying a message the real
 * browser never delivers.
 *
 * What DOES survive CORS sanitization is the failing element itself. A
 * `window` listener registered on the CAPTURE phase (the third argument to
 * `addEventListener`) sees the event while it is still travelling toward its
 * target — the only phase a `window` listener can see a resource-load error
 * in at all, since those events never bubble. `event.target` is the real
 * `<script>`, and its `src` is intact even though `event.message` is not.
 * Verified live: aborting the userbutton chunk produced
 * `SCRIPT src=.../userbutton_clerk.browser_....js` on the same run where a
 * message-based guard saw nothing.
 *
 * Resource-load failures also fire as a plain `Event`, NOT an `ErrorEvent` —
 * only uncaught script exceptions get the `ErrorEvent` subtype (with its
 * `message`/`error` fields). A check gated on `instanceof ErrorEvent` would
 * silently never fire for this class either, so this module deliberately does
 * not gate on it.
 *
 * State is sticky and page-lifetime only: once a chunk has failed once, its
 * script tag will not spontaneously start working, so there is nothing to
 * reset short of a full reload (which reinitializes this module anyway).
 */

/** Any chunk shipped inside the `@clerk/clerk-js` npm package's `dist/`. */
const CLERK_UI_CHUNK_SRC_MARKER = '@clerk/clerk-js';

function isClerkUiChunkFailureTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLScriptElement && target.src.includes(CLERK_UI_CHUNK_SRC_MARKER);
}

type Listener = () => void;
const listeners = new Set<Listener>();
let failed = false;
let installed = false;

function handleWindowError(event: Event): void {
  if (failed) return; // sticky — nothing left to detect once we already know
  if (!isClerkUiChunkFailureTarget(event.target)) return;
  failed = true;
  for (const listener of listeners) listener();
}

function install(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  // Capture phase (third arg `true`): a resource-load error event never
  // bubbles, so this is the only phase in which a window-level listener can
  // see one at all.
  window.addEventListener('error', handleWindowError, true);
}

// Installed at import time, not on first hook mount: Clerk's own SDK can
// request a UI chunk before any component that reads this hook has rendered,
// and a listener installed lazily could miss that request's failure.
install();

function subscribe(onStoreChange: () => void): () => void {
  listeners.add(onStoreChange);
  return () => listeners.delete(onStoreChange);
}

function getSnapshot(): boolean {
  return failed;
}

function getServerSnapshot(): boolean {
  return false;
}

/** True once any `@clerk/clerk-js` UI chunk has failed to load on this page. */
export function useClerkChunkFailure(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Test-only: clears the sticky module state between cases. Never call this
 * from application code — a real chunk failure does not un-happen.
 */
export function __resetClerkChunkFailureForTests(): void {
  failed = false;
  listeners.clear();
}
